/**
 * LazyImage — performant image loader for GuestBook explore POI photos.
 * - Requests server-side thumbnail via `?w=` query param to keep payload tiny
 * - Native browser lazy loading (loading="lazy")
 * - Async decoding (decoding="async")
 * - Fetch priority hint
 * - Skeleton placeholder + smooth fade-in
 * - Auto-retries once (with cache-buster) on transient failure
 */
import { useState, useRef, useEffect } from "react";

const appendWidth = (src, width) => {
  if (!src || !width) return src;
  // Only apply to internal /api/files/ URLs (we can't resize remote ones)
  if (!src.includes("/api/files/")) return src;
  const sep = src.includes("?") ? "&" : "?";
  return `${src}${sep}w=${width}&q=70`;
};

const LazyImage = ({ src, alt, className = "", fallback, eager = false, width, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const triedRef = useRef(false);

  // Use a devicePixelRatio-aware thumbnail width to keep mobile crisp.
  const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  const effectiveWidth = width ? Math.round(width * dpr) : null;
  const finalSrc = appendWidth(src, effectiveWidth);

  // Reset loaded/errored state when src changes
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
    triedRef.current = false;
  }, [finalSrc]);

  if (!src || errored) {
    return fallback || null;
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={rest.style}>
      {/* Skeleton shimmer placeholder until loaded */}
      {!loaded && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-zinc-100 to-zinc-200 animate-pulse"
          aria-hidden="true"
        />
      )}
      <img
        key={retryKey}
        src={finalSrc}
        alt={alt || ""}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "low"}
        referrerPolicy="no-referrer"
        className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (!triedRef.current) {
            triedRef.current = true;
            setRetryKey((k) => k + 1);
          } else {
            setErrored(true);
          }
        }}
      />
    </div>
  );
};

export default LazyImage;
